import React, { createContext, useContext, useEffect, useState } from 'react';
import { Amplify } from 'aws-amplify';
import { getCurrentUser, signOut, fetchAuthSession, signInWithRedirect } from 'aws-amplify/auth';
import awsConfig from '../config/aws-config';

// Configure Amplify
Amplify.configure(awsConfig);

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAuthenticating, setIsAuthenticating] = useState(false);
  
  // Cache for user data to prevent multiple API calls
  const [userDataCache, setUserDataCache] = useState(new Map());

  useEffect(() => {
    checkAuthState();
  }, []);

  const checkAuthState = async () => {
    if (isAuthenticating) {
      console.log('Authentication already in progress, skipping...');
      return;
    }
    
    setIsAuthenticating(true);
    try {
      const currentUser = await getCurrentUser();
      console.log('Current user from Cognito:', currentUser);
      
      // Get the auth session to access tokens
      const session = await fetchAuthSession();
      console.log('Auth session:', session);
      
      // Extract user attributes from ID token
      const idToken = session.tokens?.idToken;
      const attributes = idToken?.payload || {};
      console.log('User attributes from ID token:', attributes);
      
      // Combine user info with attributes from ID token
      const userWithAttributes = {
        ...currentUser,
        attributes: attributes,
        signInUserSession: session // Add session for token access
      };
      
      // Enhance user object with computed properties (now async)
      const enhancedUser = await enhanceUserData(userWithAttributes);
      console.log('Enhanced user data:', enhancedUser);
      setUser(enhancedUser);
    } catch (error) {
      console.log('No authenticated user:', error);
      setUser(null);
    } finally {
      setLoading(false);
      setIsAuthenticating(false);
    }
  };

  const enhanceUserData = async (cognitoUser) => {
    console.log('enhanceUserData called with:', cognitoUser);
    
    if (!cognitoUser) {
      console.log('No cognitoUser provided, returning null');
      return null;
    }

    const attributes = cognitoUser.attributes || {};
    const email = attributes.email || '';
    const userId = cognitoUser.userId || cognitoUser.username;
    
    console.log('Extracted userId:', userId);
    console.log('Extracted email:', email);

    // Check cache first to prevent multiple API calls
    if (userDataCache.has(userId)) {
      console.log('Using cached user data for userId:', userId);
      const cachedData = userDataCache.get(userId);
      return {
        ...cognitoUser,
        ...cachedData
      };
    }

    try {
      // Get the ID token from the session (Amplify v6 format)
      const session = cognitoUser.signInUserSession;
      console.log('Session object:', session);
      
      const idToken = session?.tokens?.idToken;
      console.log('ID Token object:', idToken);
      
      const tokenString = idToken?.toString(); // Convert token to string
      console.log('Token string length:', tokenString ? tokenString.length : 'No token');

      if (!tokenString) {
        console.warn('No ID token found, using default role');
        const defaultUserData = {
          email: email,
          name: attributes.name || `${attributes.given_name || ''} ${attributes.family_name || ''}`.trim(),
          picture: attributes.picture || '',
          bio: '', // Default empty bio when no token
          role: 'user',
          isAdmin: false,
          attributes: {
            ...attributes,
            bio: '' // Also add to attributes for consistency
          }
        };
        
        // Cache the default data
        setUserDataCache(prev => new Map(prev).set(userId, defaultUserData));
        
        return {
          ...cognitoUser,
          ...defaultUserData
        };
      }

      // Get user profile from backend to determine role
      console.log('Making API call to get user profile for userId:', userId);
      console.log('Using token:', tokenString ? 'Token present' : 'No token');
      
      const response = await fetch(`${awsConfig.API.REST.api.endpoint}/users/${userId}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${tokenString}`,
          'Content-Type': 'application/json'
        }
      });

      console.log('API response status:', response.status);
      console.log('API response ok:', response.ok);

      if (response.ok) {
        const userData = await response.json();
        console.log('API response data:', userData);
        console.log('Full user data object:', JSON.stringify(userData, null, 2));
        const userRole = userData.data?.role || userData.role || 'user';
        const isAdmin = userRole === 'admin';
        const isPending = userRole === 'no_role';

        console.log('Extracted role:', userRole);
        console.log('Is admin:', isAdmin);
        console.log('Is pending approval:', isPending);

        // If user is pending approval, we'll still return the user object
        // but mark them as pending so the UI can handle it appropriately
        const enhancedUserData = {
          // Add computed properties for easier access
          email: email,
          name: attributes.name || `${attributes.given_name || ''} ${attributes.family_name || ''}`.trim(),
          picture: attributes.picture || '',
          bio: userData.data?.bio || userData.bio || '', // Add bio from backend response
          role: userRole,
          isAdmin: isAdmin,
          isPending: isPending,
          canAccessPlatform: userRole === 'user' || userRole === 'admin',
          // Keep original attributes for full access
          attributes: {
            ...attributes,
            bio: userData.data?.bio || userData.bio || '' // Also add to attributes for consistency
          }
        };
        
        // Cache the successful response
        setUserDataCache(prev => new Map(prev).set(userId, enhancedUserData));
        
        return {
          ...cognitoUser,
          ...enhancedUserData
        };
      } else {
        // If backend call fails, assume pending approval for safety
        console.warn('Failed to get user role from backend, assuming pending approval');
        const fallbackUserData = {
          email: email,
          name: attributes.name || `${attributes.given_name || ''} ${attributes.family_name || ''}`.trim(),
          picture: attributes.picture || '',
          bio: '', // Default empty bio when backend fails
          role: 'no_role',
          isAdmin: false,
          isPending: true,
          canAccessPlatform: false,
          attributes: {
            ...attributes,
            bio: '' // Also add to attributes for consistency
          }
        };
        
        // Cache the fallback data (but with shorter TTL in a real implementation)
        setUserDataCache(prev => new Map(prev).set(userId, fallbackUserData));
        
        return {
          ...cognitoUser,
          ...fallbackUserData
        };
      }
    } catch (error) {
      console.error('Error getting user role from backend:', error);
      // Fall back to basic user data
      return {
        ...cognitoUser,
        email: email,
        name: attributes.name || `${attributes.given_name || ''} ${attributes.family_name || ''}`.trim(),
        picture: attributes.picture || '',
        role: 'user',
        isAdmin: false,
        attributes: attributes
      };
    }
  };

  const refreshUser = async () => {
    if (isAuthenticating) {
      console.log('Authentication already in progress, waiting...');
      // Wait for current authentication to complete
      while (isAuthenticating) {
        await new Promise(resolve => setTimeout(resolve, 100));
      }
      return user;
    }
    
    setIsAuthenticating(true);
    try {
      const currentUser = await getCurrentUser();
      console.log('Refreshed user from Cognito:', currentUser);
      
      // Get the auth session to access tokens
      const session = await fetchAuthSession();
      console.log('Refreshed auth session:', session);
      
      // Extract user attributes from ID token
      const idToken = session.tokens?.idToken;
      const attributes = idToken?.payload || {};
      console.log('Refreshed user attributes from ID token:', attributes);
      
      // Combine user info with attributes from ID token
      const userWithAttributes = {
        ...currentUser,
        attributes: attributes,
        signInUserSession: session // Add session for token access
      };
      
      const enhancedUser = await enhanceUserData(userWithAttributes);
      console.log('Enhanced refreshed user data:', enhancedUser);
      setUser(enhancedUser);
      return enhancedUser;
    } catch (error) {
      console.log('No authenticated user during refresh:', error);
      setUser(null);
      throw error;
    } finally {
      setIsAuthenticating(false);
    }
  };

  const login = async () => {
    try {
      await signInWithRedirect({ provider: 'Google' });
    } catch (error) {
      console.error('Error signing in:', error);
      throw error;
    }
  };

  const logout = async () => {
    try {
      await signOut();
      setUser(null);
      
      // Clear all cached user data on logout
      setUserDataCache(new Map());
      
      // Comprehensive cache clearing
      try {
        // Clear localStorage
        localStorage.clear();
        
        // Clear sessionStorage
        sessionStorage.clear();
        
        // Clear all cookies for the current domain
        document.cookie.split(";").forEach(function(c) { 
          document.cookie = c.replace(/^ +/, "").replace(/=.*/, "=;expires=" + new Date().toUTCString() + ";path=/"); 
        });
        
        // Clear browser cache if supported
        if ('caches' in window) {
          caches.keys().then(names => {
            names.forEach(name => caches.delete(name));
          }).catch(err => console.warn('Cache clearing failed:', err));
        }
        
        console.log('Successfully cleared all cached data on logout');
      } catch (cacheError) {
        console.warn('Some cache clearing operations failed:', cacheError);
      }
      
    } catch (error) {
      console.error('Error signing out:', error);
      throw error;
    }
  };

  const updateUser = (userData) => {
    if (user) {
      const updatedUser = { ...user, ...userData };
      setUser(updatedUser);
      
      // Clear cache for this user to force refresh on next auth check
      const userId = user.userId || user.username;
      if (userId) {
        setUserDataCache(prev => {
          const newCache = new Map(prev);
          newCache.delete(userId);
          return newCache;
        });
      }
    }
  };

  const clearUserCache = (userId = null) => {
    if (userId) {
      // Clear specific user from cache
      setUserDataCache(prev => {
        const newCache = new Map(prev);
        newCache.delete(userId);
        return newCache;
      });
    } else {
      // Clear entire cache
      setUserDataCache(new Map());
    }
  };

  const value = {
    user,
    loading,
    isAuthenticated: !!user,
    login,
    logout,
    updateUser,
    checkAuthState,
    refreshUser,
    clearUserCache
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
